'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Home,
  MessageCircle,
  Heart,
  Rss,
  Users,
  Zap,
  User,
  LifeBuoy,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDMStore } from '@/hooks/useDMNotifications';

interface NavItem {
  icon: React.ElementType;
  label: string;
  href: string;
  authRequired?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { icon: Home,          label: 'Home',       href: '/' },
  { icon: MessageCircle, label: 'Chat',        href: '/chat' },
  { icon: Heart,         label: 'Meet Girls',  href: '/chat?filter=female' },
  { icon: Rss,           label: 'Feed',        href: '/feed' },
  { icon: Users,         label: 'Friends',     href: '/friends',  authRequired: true },
  { icon: Zap,           label: 'Premium',     href: '/subscribe' },
  { icon: User,          label: 'Profile',     href: '/profile',  authRequired: true },
  { icon: LifeBuoy,      label: 'Support',     href: '/support' },
];

/* Separator shown before these hrefs */
const SEPARATORS_BEFORE = new Set(['/subscribe']);

export function NavDock({ isAuthenticated }: { isAuthenticated: boolean }) {
  const pathname = usePathname();
  const dmTotal = useDMStore((s) => s.total);

  const items = NAV_ITEMS.filter(
    (item) => !item.authRequired || isAuthenticated
  );

  return (
    <nav
      aria-label="Main navigation"
      className="flex items-center gap-1 rounded-2xl bg-white/[0.05] border border-white/[0.08] backdrop-blur-md px-3 py-2"
    >
      {items.map((item) => {
        const Icon = item.icon;
        const basePath = item.href.split('?')[0];
        const isActive =
          item.href === '/' ? pathname === '/' : pathname.startsWith(basePath);
        const showBadge = item.href === '/friends' && dmTotal > 0;

        return (
          <React.Fragment key={item.href}>
            {SEPARATORS_BEFORE.has(item.href) && (
              <span className="mx-1 h-4 w-px bg-white/10 shrink-0" aria-hidden />
            )}

            <Link
              href={item.href}
              aria-label={item.label}
              className="group relative flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/[0.08]"
            >
              <Icon
                size={16}
                strokeWidth={2}
                className={cn(
                  'transition-colors duration-150',
                  isActive ? 'text-brand-lime' : 'text-white/50 group-hover:text-white/90'
                )}
              />

              {/* DM unread badge */}
              {showBadge && (
                <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-0.5 rounded-full bg-brand-red text-white text-[9px] font-bold flex items-center justify-center ring-1 ring-brand-black">
                  {dmTotal > 9 ? '9+' : dmTotal}
                </span>
              )}

              {/* Active indicator */}
              {isActive && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-[3px] w-[3px] rounded-full bg-brand-lime" />
              )}

              {/* Tooltip */}
              <span
                className={cn(
                  'pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap',
                  'rounded-lg bg-brand-surface border border-white/[0.08] px-2 py-0.5',
                  'text-[10px] font-medium text-white/60 shadow-lg',
                  'opacity-0 translate-y-1 transition-all duration-150',
                  'group-hover:opacity-100 group-hover:translate-y-0',
                  'z-50'
                )}
              >
                {item.label}
              </span>
            </Link>
          </React.Fragment>
        );
      })}
    </nav>
  );
}
